import React, { useState, useCallback, useEffect } from 'react';
import './App.css';

const COLORS = ['red', 'green', 'yellow', 'blue'];
const COLOR_HEX = { red: '#e74c3c', green: '#27ae60', yellow: '#f1c40f', blue: '#2980b9' };
const DARK_HEX = { red: '#c0392b', green: '#1e8449', yellow: '#d4ac0f', blue: '#1f618d' };
const HOME_POSITIONS = { red: [2,2,2,3,3,2,3,3], green: [2,11,2,12,3,11,3,12], yellow: [11,2,11,3,12,2,12,3], blue: [11,11,11,12,12,11,12,12] };
const START_POS = { red: 0, green: 13, yellow: 39, blue: 26 };
const PATH_LENGTH = 52;

// Generate the 52-cell main path (clockwise)
const MAIN_PATH = [
  [6,1],[6,2],[6,3],[6,4],[6,5],
  [5,6],[4,6],[3,6],[2,6],[1,6],[0,6],
  [0,7],[0,8],
  [1,8],[2,8],[3,8],[4,8],[5,8],
  [6,9],[6,10],[6,11],[6,12],[6,13],[6,14],
  [7,14],[8,14],
  [8,13],[8,12],[8,11],[8,10],[8,9],
  [9,8],[10,8],[11,8],[12,8],[13,8],[14,8],
  [14,7],[14,6],
  [13,6],[12,6],[11,6],[10,6],[9,6],
  [8,5],[8,4],[8,3],[8,2],[8,1],[8,0],
  [7,0],[6,0]
];

const HOME_LANES = {
  red: [[7,1],[7,2],[7,3],[7,4],[7,5],[7,6]],
  green: [[1,7],[2,7],[3,7],[4,7],[5,7],[6,7]],
  yellow: [[13,7],[12,7],[11,7],[10,7],[9,7],[8,7]],
  blue: [[7,13],[7,12],[7,11],[7,10],[7,9],[7,8]]
};

const SAFE_POSITIONS = [0, 8, 13, 21, 26, 34, 39, 47];

function Dice({ value, rolling, onClick, disabled, currentColor }) {
  const dots = {
    1: [[50,50]],
    2: [[25,25],[75,75]],
    3: [[25,25],[50,50],[75,75]],
    4: [[25,25],[75,25],[25,75],[75,75]],
    5: [[25,25],[75,25],[50,50],[25,75],[75,75]],
    6: [[25,25],[75,25],[25,50],[75,50],[25,75],[75,75]]
  };
  return (
    <div className={`dice-container ${disabled ? 'disabled' : ''}`} onClick={disabled ? undefined : onClick}>
      <svg viewBox="0 0 100 100" className={`dice ${rolling ? 'rolling' : 'idle'}`}>
        <defs>
          <linearGradient id="diceGrad" x1="0%" y1="0%" x2="100%" y2="100%">
            <stop offset="0%" stopColor="#fff" />
            <stop offset="100%" stopColor="#e8e8e8" />
          </linearGradient>
          <filter id="diceShadow"><feDropShadow dx="2" dy="2" stdDeviation="3" floodOpacity="0.3"/></filter>
        </defs>
        <rect x="5" y="5" width="90" height="90" rx="15" fill="url(#diceGrad)" stroke="#bbb" strokeWidth="2" filter="url(#diceShadow)"/>
        {(dots[value] || dots[1]).map(([cx, cy], i) => (
          <circle key={i} cx={cx} cy={cy} r="9" fill={COLOR_HEX[currentColor] || '#333'} />
        ))}
      </svg>
      <div className="dice-label">🎲 Click to Roll!</div>
    </div>
  );
}

function Token({ color, onClick, highlight }) {
  return (
    <div className={`token ${highlight ? 'highlight' : ''}`} onClick={onClick}
      style={{ background: `radial-gradient(circle at 35% 35%, ${COLOR_HEX[color]}, ${DARK_HEX[color]})`, border: `2px solid ${DARK_HEX[color]}`, cursor: highlight ? 'pointer' : 'default' }}>
      <div className="token-shine" />
    </div>
  );
}

function getTokenPosition(color, tokenState) {
  if (tokenState.finished) return null;
  if (!tokenState.inPlay) {
    const idx = tokenState.homeSlot;
    return { row: HOME_POSITIONS[color][idx * 2], col: HOME_POSITIONS[color][idx * 2 + 1] };
  }
  if (tokenState.homeLane >= 0) return { row: HOME_LANES[color][tokenState.homeLane][0], col: HOME_LANES[color][tokenState.homeLane][1] };
  const absPos = (START_POS[color] + tokenState.steps) % PATH_LENGTH;
  return { row: MAIN_PATH[absPos][0], col: MAIN_PATH[absPos][1] };
}

function canMove(color, token, dice) {
  if (token.finished) return false;
  if (!token.inPlay) return dice === 6;
  if (token.homeLane >= 0) {
    const target = token.homeLane + dice;
    return target <= 5;
  }
  const newSteps = token.steps + dice;
  if (newSteps > 51) {
    const homeLaneEntry = newSteps - 51;
    return homeLaneEntry <= 6;
  }
  return true;
}

function initState(numPlayers = 4) {
  const activeMap = { 2: ['red', 'blue'], 3: ['red', 'yellow', 'blue'], 4: COLORS };
  const active = activeMap[numPlayers];
  const players = {};
  COLORS.forEach(c => {
    players[c] = Array.from({ length: 4 }, (_, i) => ({ inPlay: false, steps: 0, homeLane: -1, homeSlot: i, finished: false }));
  });
  return { players, activePlayers: active, current: active[0], dice: 1, rolling: false, phase: 'roll', movable: [], message: `${cap(active[0])}'s turn — Roll the dice!`, winner: null, consecutiveSixes: 0 };
}

function PlayerSelect({ onStart }) {
  const [count, setCount] = useState(4);
  const colorMap = { 2: ['red', 'blue'], 3: ['red', 'yellow', 'blue'], 4: COLORS };
  return (
    <div className="overlay">
      <div className="popup">
        <h2>🎲 Ludo 🎲</h2>
        <p>Select number of players</p>
        <div className="player-options">
          {[2, 3, 4].map(n => (
            <button key={n} className={`player-btn ${count === n ? 'selected' : ''}`} onClick={() => setCount(n)}>
              <span className="player-btn-num">{n}</span>
              <span className="player-btn-label">Players</span>
              <div className="player-btn-colors">
                {colorMap[n].map(c => <span key={c} className="mini-dot" style={{ background: COLOR_HEX[c] }} />)}
              </div>
            </button>
          ))}
        </div>
        <button className="start-btn" onClick={() => onStart(count)}>Start Game</button>
      </div>
    </div>
  );
}

export default function App() {
  const [state, setState] = useState(null);
  const [showSelect, setShowSelect] = useState(true);

  const startGame = useCallback((numPlayers) => {
    setState(initState(numPlayers));
    setShowSelect(false);
  }, []);

  const rollDice = useCallback(() => {
    if (!state || state.phase !== 'roll' || state.winner) return;
    setState(s => ({ ...s, rolling: true, phase: 'rolling' }));
    const result = Math.floor(Math.random() * 6) + 1;
    setTimeout(() => {
      setState(s => {
        const movableIdx = s.players[s.current].map((t, i) => canMove(s.current, t, result) ? i : -1).filter(i => i >= 0);
        if (movableIdx.length === 0) {
          return { ...s, dice: result, rolling: false, phase: 'waiting', movable: [], message: `${cap(s.current)} rolled ${result}. No moves!`, consecutiveSixes: result === 6 ? s.consecutiveSixes + 1 : 0 };
        }
        if (movableIdx.length === 1) {
          return applyMove({ ...s, dice: result, rolling: false, consecutiveSixes: result === 6 ? s.consecutiveSixes + 1 : 0 }, movableIdx[0]);
        }
        return { ...s, dice: result, rolling: false, phase: 'move', movable: movableIdx, message: `${cap(s.current)} rolled ${result}. Pick a token!`, consecutiveSixes: result === 6 ? s.consecutiveSixes + 1 : 0 };
      });
    }, 600);
  }, [state]);

  const selectToken = useCallback((idx) => {
    if (!state || state.phase !== 'move' || !state.movable.includes(idx)) return;
    setState(s => applyMove(s, idx));
  }, [state]);

  // Delayed turn switch
  useEffect(() => {
    if (!state || state.winner) return;
    if (state.phase !== 'waiting' && state.phase !== 'switched') return;
    const timer = setTimeout(() => {
      setState(s => {
        const ap = s.activePlayers;
        const nextPlayer = (cur) => ap[(ap.indexOf(cur) + 1) % ap.length];
        const dice = s.dice;
        const sixes = s.consecutiveSixes;
        if (sixes >= 3) {
          const next = nextPlayer(s.current);
          return { ...s, phase: 'roll', current: next, message: `${cap(next)}'s turn — Roll!`, consecutiveSixes: 0 };
        }
        const extraTurn = dice === 6;
        const next = extraTurn ? s.current : nextPlayer(s.current);
        return { ...s, phase: 'roll', current: next, message: extraTurn ? `${cap(s.current)} rolled 6! Go again!` : `${cap(next)}'s turn — Roll!`, consecutiveSixes: extraTurn ? sixes : 0 };
      });
    }, 2000);
    return () => clearTimeout(timer);
  }, [state]);

  if (showSelect) {
    return (
      <div className="app">
        <PlayerSelect onStart={startGame} />
      </div>
    );
  }

  if (!state) return null;

  const grid = Array.from({ length: 15 }, () => Array(15).fill(null));
  // Mark cell types
  const cellColors = Array.from({ length: 15 }, () => Array(15).fill(''));
  // Home bases
  for (let r = 0; r < 6; r++) for (let c = 0; c < 6; c++) cellColors[r][c] = 'red';
  for (let r = 0; r < 6; r++) for (let c = 9; c < 15; c++) cellColors[r][c] = 'green';
  for (let r = 9; r < 15; r++) for (let c = 0; c < 6; c++) cellColors[r][c] = 'yellow';
  for (let r = 9; r < 15; r++) for (let c = 9; c < 15; c++) cellColors[r][c] = 'blue';
  // Home lanes
  HOME_LANES.red.forEach(([r, c]) => cellColors[r][c] = 'red-lane');
  HOME_LANES.green.forEach(([r, c]) => cellColors[r][c] = 'green-lane');
  HOME_LANES.yellow.forEach(([r, c]) => cellColors[r][c] = 'yellow-lane');
  HOME_LANES.blue.forEach(([r, c]) => cellColors[r][c] = 'blue-lane');
  // Center
  [6,7,8].forEach(r => [6,7,8].forEach(c => cellColors[r][c] = 'center'));

  // Place tokens
  const tokenElements = [];
  state.activePlayers.forEach(color => {
    state.players[color].forEach((token, idx) => {
      const pos = getTokenPosition(color, token);
      if (!pos) return;
      const highlight = state.phase === 'move' && color === state.current && state.movable.includes(idx);
      tokenElements.push({ ...pos, color, idx, highlight });
    });
  });

  return (
    <div className="app">
      <h1 className="title">🎲 Ludo 🎲</h1>
      <div className="game-area">
        <div className="board">
          {grid.map((row, r) => row.map((_, c) => {
            const cc = cellColors[r][c];
            const isSafe = MAIN_PATH.some(([pr, pc], i) => pr === r && pc === c && SAFE_POSITIONS.includes(i));
            const isStart = Object.entries(START_POS).some(([col, si]) => MAIN_PATH[si][0] === r && MAIN_PATH[si][1] === c);
            return (
              <div key={`${r}-${c}`} className={`cell ${cc} ${isSafe ? 'safe' : ''} ${isStart ? 'start' : ''}`}
                style={{ gridRow: r + 1, gridColumn: c + 1 }}>
                {isSafe && !isStart && <span className="safe-star">★</span>}
                {isStart && <span className="safe-star">▶</span>}
                {tokenElements.filter(t => t.row === r && t.col === c).map(t => (
                  <Token key={`${t.color}-${t.idx}`} color={t.color} highlight={t.highlight}
                    onClick={() => t.highlight && selectToken(t.idx)} />
                ))}
              </div>
            );
          }))}
        </div>
        <div className="side-panel">
          <div className={`turn-indicator ${state.current}`}>
            <div className="turn-dot" style={{ background: COLOR_HEX[state.current] }} />
            {cap(state.current)}'s Turn
          </div>
          <Dice value={state.dice} rolling={state.rolling} onClick={rollDice}
            disabled={state.phase !== 'roll' || !!state.winner} currentColor={state.current} />
          <div className="message">{state.message}</div>
          <div className="scoreboard">
            {state.activePlayers.map(c => (
              <div key={c} className="score-row">
                <span className="score-dot" style={{ background: COLOR_HEX[c] }} />
                <span>{cap(c)}</span>
                <span>{state.players[c].filter(t => t.finished).length}/4</span>
              </div>
            ))}
          </div>
          {state.winner && <div className="winner">🏆 {cap(state.winner)} Wins! 🏆</div>}
          <button className="reset-btn" onClick={() => setShowSelect(true)}>New Game</button>
        </div>
      </div>
    </div>
  );
}

function cap(s) { return s.charAt(0).toUpperCase() + s.slice(1); }

function applyMove(s, idx) {
  const color = s.current;
  const players = JSON.parse(JSON.stringify(s.players));
  const token = players[color][idx];
  const dice = s.dice;

  if (!token.inPlay) {
    token.inPlay = true;
    token.steps = 0;
    // Capture check at start
    const absPos = START_POS[color];
    captureAt(players, color, MAIN_PATH[absPos][0], MAIN_PATH[absPos][1]);
  } else if (token.homeLane >= 0) {
    token.homeLane += dice;
    if (token.homeLane >= 5) { token.finished = true; }
  } else {
    const newSteps = token.steps + dice;
    if (newSteps > 51) {
      token.homeLane = newSteps - 52;
      if (token.homeLane >= 5) { token.finished = true; }
    } else {
      token.steps = newSteps;
      const absPos = (START_POS[color] + token.steps) % PATH_LENGTH;
      if (!SAFE_POSITIONS.includes(absPos)) {
        captureAt(players, color, MAIN_PATH[absPos][0], MAIN_PATH[absPos][1]);
      }
    }
  }

  const winner = players[color].every(t => t.finished) ? color : null;
  if (winner) {
    return { ...s, players, phase: 'roll', movable: [], message: `🏆 ${cap(color)} wins!`, winner };
  }
  const msg = dice === 6 ? `${cap(color)} rolled 6!` : `${cap(color)} moved.`;
  return { ...s, players, phase: 'switched', movable: [], message: msg, winner: null };
}

function captureAt(players, currentColor, row, col) {
  COLORS.forEach(c => {
    if (c === currentColor) return;
    players[c].forEach(t => {
      if (!t.inPlay || t.finished || t.homeLane >= 0) return;
      const absPos = (START_POS[c] + t.steps) % PATH_LENGTH;
      if (MAIN_PATH[absPos][0] === row && MAIN_PATH[absPos][1] === col) {
        t.inPlay = false;
        t.steps = 0;
      }
    });
  });
}
